import MultipleLinesChart from '@Components/UI/Charts/MultipleLinesChart/MultipleLinesChart';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import DateRangePickerPopup, {
	DateRangeOption,
} from '@Components/UI/Pickers/DateRangePickerPopup/DateRangePickerPopup';
import MonthRangePicker from '@Components/UI/Pickers/MonthRangePicker/MonthRangePicker';
import YearRangePicker from '@Components/UI/Pickers/YearRangePicker/YearRangePicker';
import {
	AnalyticsCardsContainer,
	EnrollmentTypeCard,
	FormActionsWrapper,
	FormControlsWrapper,
	StyledIconButton,
	TypeInfo,
	TypeName,
	TypeValue,
} from '@Components/UI/Analytics/Analytics.styled';
import { Button, Icon, MenuItem } from '@mui/material';
import { AnalyticsSlices } from '@Pages/ClassRooms/Constants/Classroom.constants';
import CourseUnitsAnalytics from '@Pages/ClassRooms/Pages/SingleCourse/Pages/Analytics/CourseUnitsAnalytics/CourseUnitsAnalytics';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import {
	GraphSlice,
	GraphSliceType,
} from '@Services/classrooms/classrooms.res.types';
import { Spacer } from '@Styled/utilities';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import useStandalonePageLoader from './useStandalonePageLoader';

type LectureAnalyticsData = {
	total_enrollments: number;
	active_enrollments: number;
	expired_enrollments: number;
	completed_enrollments: number;
	average_progress: number;
	units_count: number;
	learning_time: number;
	graph: GraphSlice[];
};

type FormValues = {
	slice: GraphSliceType;
	dates: Date[];
};

const StandaloneLectureAnalytics: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const [data, setData] = useState<LectureAnalyticsData>();
	const [dateRange, setDateRange] = useState<DateRangeOption>('last 2 weeks');
	const [dateOpen, setDateOpen] = useState(false);
	const [dateAnchor, setDateAnchor] = useState<HTMLElement>();
	const defaults = useMemo<FormValues>(() => {
		const start = new Date();
		start.setDate(start.getDate() - 14);
		return { slice: 'day', dates: [start, new Date()] };
	}, []);
	const { control, watch, setValue, reset, getValues } = useForm<FormValues>({
		defaultValues: defaults,
	});
	const selectedSlice = watch('slice');

	const load = useCallback(
		async (form?: FormValues) => {
			const values = form || getValues();
			try {
				await withLoading(async () => {
					const response = await api.post('standalone-courses/lectures/analytics', {
						lecture_id: Number(id),
						from: values.dates[0],
						to: values.dates[1],
						slice: values.slice,
					});
					setData(response.data.data);
				});
			} catch (error: any) {
				displaySnackbar(
					'error',
					error.response?.data?.message || 'Unable to load Lecture analytics'
				);
			}
		},
		[displaySnackbar, getValues, id, withLoading]
	);

	useEffect(() => {
		load(defaults);
	}, [defaults, load]);

	const changeDateRange = (type: DateRangeOption) => {
		setDateRange(type);
		if (type === 'custom') return;
		const start = new Date();
		const end = new Date();
		if (type === 'last 2 weeks') start.setDate(start.getDate() - 14);
		if (type === 'last month') start.setMonth(start.getMonth() - 1);
		if (type === 'last 3 months') start.setMonth(start.getMonth() - 2);
		setValue('dates', [start, end]);
	};

	const resetFilters = () => {
		reset(defaults);
		setDateRange('last 2 weeks');
		load(defaults);
	};

	const metrics = data
		? [
				{
					label: 'Started',
					value: data.total_enrollments,
					icon: 'school',
					color: '#6C63FF',
				},
				{
					label: 'Active',
					value: data.active_enrollments,
					icon: 'play_circle',
					color: '#00CE67',
				},
				{
					label: 'Expired',
					value: data.expired_enrollments,
					icon: 'timer_off',
					color: '#E53935',
				},
				{
					label: 'Completed',
					value: data.completed_enrollments,
					icon: 'task_alt',
					color: '#5AC0FC',
				},
				{
					label: 'Average Progress',
					value: `${Math.round(Number(data.average_progress || 0))}%`,
					icon: 'trending_up',
					color: '#FF8532',
				},
				{
					label: 'Units',
					value: data.units_count,
					icon: 'view_module',
					color: '#FFD037',
				},
				{
					label: 'Learning Time',
					value: formatLearningTime(data.learning_time),
					icon: 'schedule',
					color: '#26A69A',
				},
		  ]
		: [];

	return (
		<AnalyticsPage>
			<ResponsiveAnalyticsCards>
				{metrics.map((metric) => (
					<EnrollmentTypeCard key={metric.label} color={metric.color}>
						<TypeInfo>
							<Icon>{metric.icon}</Icon>
							<TypeValue>{metric.value}</TypeValue>
						</TypeInfo>
						<TypeName>{metric.label}</TypeName>
					</EnrollmentTypeCard>
				))}
			</ResponsiveAnalyticsCards>

			<FormControlsWrapper>
				<EdFormControl select name="slice" control={control}>
					{AnalyticsSlices.map((slice) => (
						<MenuItem key={slice.value} value={slice.value}>
							{slice.label}
						</MenuItem>
					))}
				</EdFormControl>
				<Spacer mx={14} />
				<StyledIconButton
					onClick={(event) => {
						setDateOpen(!dateOpen);
						setDateAnchor(event.currentTarget);
					}}
				>
					<Icon>event</Icon>
				</StyledIconButton>
				<DateRangePickerPopup
					open={dateOpen}
					anchorEl={dateAnchor}
					onClose={() => setDateOpen(false)}
					onChange={changeDateRange}
					rangeOptionValue={dateRange}
				>
					<DateRangePickerPopup.Body>
						{selectedSlice === 'month' && (
							<MonthRangePicker control={control as any} name="dates" />
						)}
						{selectedSlice === 'year' && (
							<YearRangePicker control={control as any} name="dates" />
						)}
					</DateRangePickerPopup.Body>
				</DateRangePickerPopup>
			</FormControlsWrapper>

			<FormActionsWrapper>
				<Button onClick={resetFilters} variant="outlined">
					Reset
				</Button>
				<Spacer mx="1rem" />
				<Button
					onClick={() => load()}
					variant="contained"
				>
					Apply
				</Button>
			</FormActionsWrapper>

			<MultipleLinesChart
				sliceType={selectedSlice}
				colors={() => '#6C63FF'}
				data={data?.graph || []}
			/>

			<CourseUnitsAnalytics />
		</AnalyticsPage>
	);
};

const formatLearningTime = (seconds: number) => {
	if (!seconds) return '0m';
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);
	if (days) return `${days}d ${hours % 24}h`;
	if (hours) return `${hours}h ${minutes % 60}m`;
	return `${minutes}m`;
};

const AnalyticsPage = styled.div`
	width: 100%;
	max-width: 100%;
	min-width: 0;
	overflow-x: hidden;
`;

const ResponsiveAnalyticsCards = styled(AnalyticsCardsContainer)`
	width: 100%;
	max-width: 100%;
	flex-wrap: wrap;
	gap: 1rem;

	> * {
		flex: 1 1 12rem;
		min-width: 12rem;
		margin: 0;
	}
`;

export default StandaloneLectureAnalytics;
